// File: src/Components/payments/PaymentDialog.jsx

import React, { useState, useEffect,useContext } from 'react';
import PropTypes from 'prop-types';
import {
    Modal,
    Box,
    Typography,
    DialogContent,
    DialogActions,
    Button,
    Alert,
    RadioGroup,
    Radio,
    FormControl,
    FormControlLabel,
    FormLabel,
    CircularProgress,
    ToggleButtonGroup,
    ToggleButton
} from '@mui/material';
import CheckIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentIcon from '@mui/icons-material/Payment';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CreditCardForm from './parts/CreditCardForm';
import ManualPaymentDialog from './ManualPaymentDialog';
import PaymentService from '../../Services/payment.service';
import AuthService from '../../Services/auth.service.js';
import logOut from '../../assests/images/Hamburger/logout.svg';
import { PermissionsContext } from '../../Contexts/Permissions.js';
const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 800,
    bgcolor: 'background.paper',
    borderRadius: 3,
    boxShadow: 24,
    px: 4,
    py: 3
};

const validateFields = (values) => {
    const errors = {};

    const cleanedCardNumber = values.cardNumber.replace(/\s+/g, '');

    // Validate card number: digits only, 13 to 19 digits
    if (!/^\d{13,19}$/.test(cleanedCardNumber)) {
        errors.cardNumber = 'Invalid card number';
    }

    // Validate month: must be 2 digits and between 01-12
    const month = parseInt(values.month, 10);
    if (!values.month || isNaN(month) || month < 1 || month > 12) {
        errors.month = 'Invalid month';
    }

    // Validate year: must be 2 digits and >= 23 (adjust based on your logic)
    const year = parseInt(values.year, 10);
    if (!values.year || isNaN(year) || year < 23 || year > 99) {
        errors.year = 'Invalid year';
    }

    // Validate CVV: must be exactly 3 digits
    if (!/^\d{3}$/.test(values.cvv)) {
        errors.cvv = 'CVV must be 3 digits';
    }

    return errors;
};

const PaymentDialog = ({
    open,
    amount = 0,
    yearly = false,
    allowYearly = false,
    userId,
    userType,
    onSuccess,
    onPlanChange = () => { },
    onClose
}) => {
    const { t, i18n } = useTranslation();
    const dir = i18n.dir();
    const navigate = useNavigate();
    const [plan, setPlan] = useState('monthly');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [paid, setPaid] = useState(false);
    const [errors, setErrors] = useState({});
    const [paymentType, setPaymentType] = useState('creditCard'); // 'creditCard' or 'manual'
    const [showManualDialog, setShowManualDialog] = useState(false);
    const { permissions, permissionsState, loading: permissionsLoading, user, userTypeContext, token, isAccountant, resetPermissionsState } = useContext(PermissionsContext);

    
    const [values, setValues] = useState({
        cardNumber: '',
        month: '',
        year: '',
        cvv: '',
        holder: ''
    });

    const hideToggle = amount <= 0;               // ⇠ your flag
    const canShowPlanToggle = allowYearly && !hideToggle && amount > 0;

    useEffect(() => {
        setPlan(yearly ? 'yearly' : 'monthly');
    }, [yearly]);

    const svc = +userType === 2 ? PaymentService.accountant : PaymentService.client;

    const handlePlanChange = (e) => {
        const newPlan = e.target.value;
        setPlan(newPlan);
        setError('');
        onPlanChange(newPlan === 'yearly');
    };

    const handlePaymentTypeChange = (event, newPaymentType) => {
        if (newPaymentType !== null) {
            setPaymentType(newPaymentType);
            setError('');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        const formatCardNumber = (input) =>
            input
                .replace(/\D/g, '')             // Remove non-digits
                .replace(/(.{4})/g, '$1 ')      // Insert space every 4 digits
                .trim();                        // Remove trailing space

        setValues((prev) => ({
            ...prev,
            [name]: name === 'cardNumber' ? formatCardNumber(value) : value
        }));

        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handlePay = async () => {
        if (paymentType === 'manual') {
            setShowManualDialog(true);
            return;
        }

        const fieldErrors = validateFields(values);
        if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
            return;
        }

        setBusy(true);
        setError('');
        const cleanedCardNumber = values.cardNumber.replace(/\s+/g, '');

        const dto = {
            Payment: {
                UserId: userId,
                UserType: userType,
                Amount: 0,
                CardNumber: cleanedCardNumber,
                ExpiryMonth: +values.month,
                ExpiryYear: +values.year,
                CVV: values.cvv,
                CardHolderId: values.holder,
                Items: [{
                    Name: t('XtramAccountingSystem'),
                    Type: 'I',
                    UnitPrice: amount,
                    UnitsNumber: 1
                }]
            },
            IsYearly: plan === 'yearly'
        };

        try {
            await svc.startPaid(dto);
            setPaid(true);
            setTimeout(onSuccess, 1500);
        } catch (ex) {
            // Handle different types of error messages
            let errorMessage = ex.message || t('PaymentFailed');
            
            // If the error message contains specific error codes, use them directly
            if (ex.message && (ex.message.includes('Card declined by issuer') || ex.message.match(/\[\d+\]/))) {
                errorMessage = ex.message;
            }
            
            setError(errorMessage);
        } finally {
            setBusy(false);
        }
    };

    const handleManualPaymentSuccess = () => {
        setPaid(true);
        setTimeout(onSuccess, 1500);
    };

    const handleLogout = async () => {
        try {
            resetPermissionsState()
            await AuthService.logout();
            navigate('/login');
        } catch (error) {
            // Optionally handle any errors
        }
    };

    const isFormValid = Object.keys(validateFields(values)).length === 0;

    // Function to parse and translate error messages
    

    return (
        <>
            <Modal open={open} onClose={onClose} disableEscapeKeyDown>
                <Box sx={modalStyle} dir={dir}>
                    <Typography variant="h6" gutterBottom>
                        {paid ? t('PaymentAccepted') : t('CompleteYourPayment')}
                    </Typography>

                    <DialogContent dividers>
                        {canShowPlanToggle && !paid && (
                            <FormControl component="fieldset" sx={{ mb: 2 }}>
                                <FormLabel component="legend">{t('ChoosePlan')}</FormLabel>
                                <RadioGroup row name="billingPlan" value={plan} onChange={handlePlanChange}>
                                    <FormControlLabel value="monthly" control={<Radio />} label={t('Monthly')} />
                                    <FormControlLabel value="yearly" control={<Radio />} label={t('YearlyWithBonus')} />
                                </RadioGroup>
                            </FormControl>
                        )}

                        {paid ? (
                            <Alert severity="success" icon={<CheckIcon fontSize="inherit" />}>
                                {t('ThankYouReloading')}
                            </Alert>
                        ) : (
                            <>
                                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                    {t('Amount')}: ₪{amount?.toFixed(2)}
                                </Typography>

                                {/* Payment Type Selection */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        {t('PaymentMethod')}
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={paymentType}
                                        exclusive
                                        onChange={handlePaymentTypeChange}
                                        aria-label="payment method"
                                    >
                                        <ToggleButton value="creditCard" aria-label="credit card">
                                            <CreditCardIcon sx={{ mr: 1 }} />
                                            {t('CreditCard')}
                                        </ToggleButton>
                                        {/* <ToggleButton value="manual" aria-label="manual payment">
                                            <PaymentIcon sx={{ mr: 1 }} />
                                            {t('ManualPayment')}
                                        </ToggleButton> */}
                                    </ToggleButtonGroup>
                                </Box>

                                {paymentType === 'creditCard' && (
                                    <CreditCardForm
                                        values={values}
                                        onChange={handleChange}
                                        disabled={busy}
                                        errors={errors}
                                    />
                                )}

                                {error && (
                                    <Alert severity="error" icon={<ErrorIcon fontSize="inherit" />} sx={{ mt: 2 }}>
                                        {error}
                                    </Alert>
                                )}
                            </>
                        )}
                    </DialogContent>

                    {!paid && (
                        <DialogActions sx={{ justifyContent: 'space-between' }}>
                            <Button
                                onClick={handlePay}
                                variant="contained"
                                color="primary"
                                disabled={busy || (paymentType === 'creditCard' && !isFormValid)}
                                startIcon={busy && <CircularProgress size={20} />}
                            >
                                {paymentType === 'creditCard' ? t('PayNow') : t('ContinueToManualPayment')}
                            </Button>
                            <Button
                                onClick={handleLogout}
                                color="error"
                                variant="outlined"
                                startIcon={<img src={logOut} alt="logout" width={20} height={20} />}
                            >
                                {t('logOut')}
                            </Button>
                        </DialogActions>
                    )}
                </Box>
            </Modal>

            {showManualDialog && (
                <ManualPaymentDialog
                    open={showManualDialog}
                    amount={amount}
                    yearly={plan === 'yearly'}
                    userId={userId}
                    userType={userType}
                    onSuccess={handleManualPaymentSuccess}
                    onClose={() => setShowManualDialog(false)}
                />
            )}
        </>
    );
};

PaymentDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    amount: PropTypes.number,
    yearly: PropTypes.bool,
    allowYearly: PropTypes.bool,
    userId: PropTypes.string.isRequired,
    userType: PropTypes.number.isRequired,
    onSuccess: PropTypes.func.isRequired,
    onPlanChange: PropTypes.func,
    onClose: PropTypes.func
};

export default PaymentDialog;